import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { reminderApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const ReminderList = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingReminders, setProcessingReminders] = useState(false);
  const [processingSuccess, setProcessingSuccess] = useState(null);
  const { hasPermission } = useAuth();

  // Fetch reminders on component mount
  useEffect(() => {
    fetchReminders();
  }, []);

  // Function to fetch reminders
  const fetchReminders = async () => {
    try {
      setLoading(true);
      const response = await reminderApi.getAllReminders();
      setReminders(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching reminders:', err);
      setError('Failed to load reminders. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Function to toggle reminder enabled status
  const handleToggleReminder = async (id, currentStatus) => {
    try {
      await reminderApi.toggleReminder(id, !currentStatus);
      // Update the local state
      setReminders(reminders.map(reminder => 
        reminder.id === id ? { ...reminder, enabled: !currentStatus } : reminder
      ));
    } catch (err) {
      console.error('Error toggling reminder:', err);
      setError('Failed to update reminder status. Please try again.');
    }
  };

  // Function to delete a reminder
  const handleDeleteReminder = async (id) => {
    if (window.confirm('Are you sure you want to delete this reminder?')) {
      try {
        await reminderApi.deleteReminder(id);
        // Remove the deleted reminder from the local state
        setReminders(reminders.filter(reminder => reminder.id !== id));
      } catch (err) {
        console.error('Error deleting reminder:', err);
        setError('Failed to delete reminder. Please try again.');
      }
    }
  };

  // Function to process all reminders
  const handleProcessReminders = async () => {
    try {
      setProcessingReminders(true);
      await reminderApi.processReminders();
      setProcessingSuccess('Reminders are being processed in the background.');
      setTimeout(() => setProcessingSuccess(null), 5000); // Clear success message after 5 seconds
    } catch (err) {
      console.error('Error processing reminders:', err);
      setError('Failed to process reminders. Please try again.');
    } finally {
      setProcessingReminders(false);
    }
  };

  // Helper function to format reminder trigger
  const formatTrigger = (trigger, days) => {
    switch (trigger) {
      case 'days_before_expiry':
        return `${days} day${days !== 1 ? 's' : ''} before expiry`;
      case 'days_after_expiry':
        return `${days} day${days !== 1 ? 's' : ''} after expiry`;
      case 'specific_day':
        return `On day ${days} of each month`;
      default:
        return trigger;
    }
  };

  // Helper function to format reminder frequency
  const formatFrequency = (frequency) => {
    switch (frequency) {
      case 'daily':
        return 'Daily';
      case 'weekly':
        return 'Weekly';
      case 'monthly':
        return 'Monthly';
      case 'once':
        return 'Once';
      default:
        return frequency;
    }
  };

  // Helper function to format reminder type
  const formatType = (type) => {
    switch (type) {
      case 'email':
        return 'Email';
      case 'telegram':
        return 'Telegram';
      default:
        return type;
    }
  };

  // Helper function to format time
  const formatTime = (timeStr) => {
    try {
      const time = new Date(`2000-01-01T${timeStr}`);
      return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return timeStr;
    }
  };

  return (
    <div className="reminder-list">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Payment Reminders</h1>
        <div className="d-flex gap-2">
          {hasPermission('reminders', 'create') && (
            <Link to="/reminders/new" className="btn btn-primary">
              <i className="fas fa-plus me-1"></i> New Reminder
            </Link>
          )}
          {hasPermission('reminders', 'update') && (
            <button 
              className="btn btn-success" 
              onClick={handleProcessReminders}
              disabled={processingReminders}
            >
              {processingReminders ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Processing...
                </>
              ) : (
                <>
                  <i className="fas fa-play me-1"></i> Process Reminders
                </>
              )}
            </button>
          )}
          <Link to="/reminder-templates" className="btn btn-outline-secondary">
            <i className="fas fa-file-alt me-1"></i> Templates
          </Link>
          <Link to="/reminder-logs" className="btn btn-outline-secondary">
            <i className="fas fa-history me-1"></i> Logs
          </Link>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {processingSuccess && (
        <div className="alert alert-success" role="alert">
          {processingSuccess}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : reminders.length === 0 ? (
        <div className="alert alert-info" role="alert">
          No reminders found. Click "New Reminder" to create one.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Trigger</th>
                <th>Frequency</th>
                <th>Send Time</th>
                <th>Status</th>
                <th>Last Run</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reminders.map(reminder => (
                <tr key={reminder.id}>
                  <td>{reminder.name}</td>
                  <td>{formatType(reminder.reminder_type)}</td>
                  <td>{formatTrigger(reminder.trigger, reminder.trigger_days)}</td>
                  <td>{formatFrequency(reminder.frequency)}</td>
                  <td>{formatTime(reminder.send_time)}</td>
                  <td>
                    <span className={`badge ${reminder.enabled ? 'bg-success' : 'bg-danger'}`}>
                      {reminder.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td>{reminder.last_run ? new Date(reminder.last_run).toLocaleDateString() : 'Never'}</td>
                  <td>
                    <div className="btn-group btn-group-sm">
                      {hasPermission('reminders', 'update') && (
                        <>
                          <Link to={`/reminders/${reminder.id}/edit`} className="btn btn-outline-primary">
                            <i className="fas fa-edit"></i>
                          </Link>
                          <button 
                            className="btn btn-outline-secondary"
                            onClick={() => handleToggleReminder(reminder.id, reminder.enabled)}
                            title={reminder.enabled ? 'Disable' : 'Enable'}
                          >
                            <i className={`fas ${reminder.enabled ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                          </button>
                          <Link to={`/reminders/${reminder.id}/test`} className="btn btn-outline-info" title="Test">
                            <i className="fas fa-vial"></i>
                          </Link>
                        </>
                      )}
                      {hasPermission('reminders', 'delete') && (
                        <button 
                          className="btn btn-outline-danger"
                          onClick={() => handleDeleteReminder(reminder.id)}
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ReminderList;
